import * as Yup from 'yup';
import { includes, isEmpty, isUndefined, some } from 'lodash-es';
import { error } from '../../utils/i18n';
import { Gender, isFreeGender } from '../../interfaces/common';

export const name = (attribute: string) =>
  Yup.string()
    .trim()
    .required(error('required', attribute))
    .max(20, error('max', attribute, { max: 20 }));

export const nameKana = (attribute: string, max: number) =>
  Yup.string()
    .trim()
    .required(error('required', attribute))
    .matches(
      /^[\u3040-\u309f\u2014\u2015\u30fc]+$/,
      error('hiragana', attribute),
    )
    .matches(
      /^[^\u3094]+$/,
      error('disallowed_chars', attribute, { disallowed_chars: '\u3094' }),
    )
    .max(max, error('max', attribute, { max }));

export const birthDate = (attribute: string) =>
  Yup.string().required(error('required', attribute));

export const gender = (attribute: string) =>
  Yup.string().required(error('required', attribute));

const genderExpressionMaxLength = 40;

// yupのStringSchema型に maxGraphemeLength を追加
declare module 'yup' {
  interface StringSchema {
    maxGraphemeLength(maxLength: number, message?: string): this;
  }
}

Yup.addMethod(
  Yup.string,
  'maxGraphemeLength',
  (maxLength: number, message?: string) => {
    const segmenter = new Intl.Segmenter('ja', { granularity: 'grapheme' });

    return Yup.string().test(
      'max-grapheme-length',
      message || `Must be at most ${maxLength} characters`,
      (value: string | undefined) =>
        value ? [...segmenter.segment(value)].length <= maxLength : true,
    );
  },
);

export const genderExpression = (
  attribute: string,
  then?: (
    schema: Yup.StringSchema<string | undefined, Yup.AnyObject, undefined, ''>,
  ) => Yup.ISchema<any>,
) =>
  Yup.string().when('gender', {
    // Enable gender_expression validation when gender is 'free'.
    is: (val: Gender) => isFreeGender(val),
    then: (schema) => {
      return (then ?? ((v) => v))(
        schema
          .required(error('required', attribute))
          .maxGraphemeLength(
            genderExpressionMaxLength,
            error('max', attribute, { max: genderExpressionMaxLength }),
          )
          .test(
            'no only space, newline, tab',
            error('required', attribute),
            (value) => {
              return !value || !/^[\s\uFEFF\xA0]+$/.test(value);
            },
          ),
      );
    },
    otherwise: (schema) => schema,
  });

export const phoneNumber = (attribute: string) =>
  Yup.string()
    .trim()
    .required(error('required', attribute))
    .matches(
      /^[0-9０-９\-ー−]{9,14}$/,
      error('range', attribute, { min: 9, max: 14 }),
    );

// Email Regex Definition
//  - Account part: ASCII characters excluding '@'
//  - Domain part: ASCII characters excluding '@' and ','
//
// NG Cases:
//   - @user@example.com
//   - user@@example.com
//   - user@example.com,user2@example.com
//   - user@example,com
//   - user@.example.com
const emailRegex = /^[!-?A-~]{1,80}@[a-zA-Z\d][!-+--?A-~]{0,79}$/;

export const email = (attribute: string) =>
  Yup.string()
    .trim()
    .required(error('required', attribute))
    .matches(emailRegex, error('invalid', attribute));

export const optionalEmail = (attribute: string) =>
  Yup.string()
    .trim()
    .matches(emailRegex, {
      message: error('invalid', attribute),
      excludeEmptyString: true,
    });

export const password = (
  attribute: string,
  passwordConfirmationAttributeName: string,
  usernameAttributeName?: string,
) =>
  Yup.string()
    .required(error('required', attribute))
    .matches(
      /^.{6,72}$/,
      error('invalid_length', attribute, { min: 6, max: 72 }),
    )
    .matches(/[a-zA-Z]/, error('password_rule'))
    .matches(/[0-9]/, error('password_rule'))
    .matches(/^[\x20-\x7E]+$/, error('password_rule'))
    .test(attribute, error('password_contains_username'), (value, context) => {
      if (isUndefined(usernameAttributeName)) return true;

      const usernameValue = context.parent[usernameAttributeName];
      return isEmpty(usernameValue) ? true : !includes(value, usernameValue);
    })
    .test(attribute, error('password_match'), (value, context) => {
      const passwordConfirmation =
        context.parent[passwordConfirmationAttributeName];
      return isEmpty(passwordConfirmation)
        ? true
        : value === passwordConfirmation;
    });

export const passwordConfirmation = (attribute: string) =>
  Yup.string().required(error('required', attribute));

export const inputtedByParents = (attribute: string) =>
  Yup.boolean().oneOf([true], error('required_other_than_input', attribute));

export const inputtedByParentsForJunior = (
  attribute: string,
  isJunior: boolean,
) => {
  const schema = Yup.boolean();
  if (isJunior) {
    return schema.oneOf([true], error('consent', attribute));
  }
  return schema;
};

export const verification = (attribute: string) =>
  Yup.object().shape({
    code: Yup.string()
      .trim()
      .required(error('required', attribute))
      .min(6, error('min', attribute, { min: 6 }))
      .max(6, error('max', attribute, { max: 6 })),
  });

export const username = (
  attribute: string,
  organizationMembershipCode?: string,
) =>
  Yup.string()
    .trim()
    .required(error('required', attribute))
    .matches(/^[0-9a-z\-_.]*$/, error('username_rule'))
    .matches(/^((?!^\d{9}$).)*$/, error('username_9digits'))
    .test(attribute, error('username_random_10digits'), (value?: string) =>
      isUndefined(value) ||
      isUndefined(organizationMembershipCode) ||
      !/^\d{10}$/.test(value)
        ? true
        : value === organizationMembershipCode,
    )
    .max(40, error('max', attribute, { max: 40 }));

export const grade = (attribute: string) =>
  Yup.object().shape({
    id: Yup.string().required(error('required', attribute)),
  });

export const className = () => Yup.string().trim();

export const attendanceNumber = (attribute: string) =>
  Yup.string()
    .trim()
    .matches(/^[0-9]*$/, error('numeric', attribute));

export const departmentCategoriesOfInterest = (
  attribute: string,
  isG12: boolean,
) => {
  const schema = Yup.array().of(Yup.object());
  if (isG12) {
    return schema.required().min(1, error('required', attribute));
  }
  return schema;
};

export const collegeWithDepartment = (attribute: string, isG12: boolean) => {
  const schema = Yup.array().compact().of(Yup.object());
  if (isG12) {
    return schema
      .required()
      .test(attribute, error('required', attribute), (value: any[]) => {
        return some(
          value,
          (v: any) => !isEmpty(v?.college) && !isEmpty(v?.college_department),
        );
      });
  }
  return schema;
};

export const consentToSchools = (attribute: string) =>
  Yup.boolean().oneOf([true], error('consent', attribute));
